import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, catchError, map, of, switchMap} from 'rxjs';
import {ClientConfig} from '../../shared/models/client-config.model';
import {ThemeDTO, ThemeIcons} from '../../shared/models/theme.model';
import {environment} from '../../../environments/environment';

@Injectable({providedIn: 'root'})
export class ClientConfigService {

  constructor(
    private http: HttpClient,
  ) {
  }

  getThemeForCurrentDomain(): Observable<ThemeDTO> {
    const brandingCodeOverride = this.resolveBrandingCodeOverride();
    const clientDomainKey = this.resolveClientDomainKey(window.location.hostname);

    return this.getResolvedClientConfig(clientDomainKey, brandingCodeOverride).pipe(
      map(config => this.toThemeDTO(config, brandingCodeOverride))
    );
  }

  getSpecialClientRedirectUrl(): Observable<string | null> {
    const brandingCodeOverride = this.resolveBrandingCodeOverride();

    if (!brandingCodeOverride) {
      return of(null);
    }

    return this.getClientDomainKeyByBrandingCode(brandingCodeOverride).pipe(
      map(resolvedClientDomainKey => {
        if (!resolvedClientDomainKey) {
          return null;
        }

        if (resolvedClientDomainKey === environment.defaultClientDomainKey) {
          return null;
        }

        const redirectUrl = new URL(
          `${window.location.pathname}${window.location.hash}`,
          `https://${resolvedClientDomainKey}${environment.defaultDomainSuffix}`
        );

        return redirectUrl.toString();
      })
    );
  }

  private getResolvedClientConfig(
    clientDomainKey: string,
    brandingCodeOverride: string | null
  ): Observable<ClientConfig> {
    if (!brandingCodeOverride) {
      return this.getClientConfigWithFallback(clientDomainKey);
    }

    return this.getClientDomainKeyByBrandingCode(brandingCodeOverride).pipe(
      switchMap(resolvedClientDomainKey => {
        if (resolvedClientDomainKey) {
          return this.getClientConfigWithFallback(resolvedClientDomainKey);
        }

        return this.getClientConfigWithFallback(clientDomainKey);
      })
    );
  }

  private getClientDomainKeyByBrandingCode(brandingCode: string): Observable<string | null> {
    return this.getBrandingCodeClientRegistry().pipe(
      map(registry => registry[brandingCode] ?? null)
    );
  }

  private getBrandingCodeClientRegistry(): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>('/clients/clients.json').pipe(
      catchError(error => {
        console.warn('Could not load clients registry.', error);
        return of({});
      })
    );
  }
  private getClientConfigWithFallback(clientDomainKey: string): Observable<ClientConfig> {
    return this.getClientConfig(clientDomainKey).pipe(
      catchError(error => {
        if (clientDomainKey === environment.defaultClientDomainKey) {
          throw error;
        }

        console.warn(
          `Client config for "${clientDomainKey}" was not found. Falling back to "${environment.defaultClientDomainKey}".`,
          error
        );

        return this.getClientConfig(environment.defaultClientDomainKey);
      })
    );
  }

  private getClientConfig(clientDomainKey: string): Observable<ClientConfig> {
    return this.http.get<ClientConfig>(`/clients/${clientDomainKey}/client.json`);
  }

  private resolveBrandingCodeOverride(): string | null {
    const normalizedHostname = window.location.hostname.toLowerCase();
    const normalizedDefaultHost = environment.defaultHost.toLowerCase();

    if (normalizedHostname !== normalizedDefaultHost) {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    return params.get(environment.brandingCodeQueryParam)?.trim() || null;
  }

  private resolveClientDomainKey(hostname: string): string {
    const normalizedHostname = hostname.toLowerCase();

    if (environment.defaultHost.toLowerCase() === normalizedHostname) {
      return environment.defaultClientDomainKey;
    }

    if (normalizedHostname.endsWith(environment.defaultDomainSuffix.toLowerCase())) {
      return normalizedHostname.slice(0, -environment.defaultDomainSuffix.length);
    }

    return environment.defaultClientDomainKey;
  }

  private toThemeDTO(config: ClientConfig, brandingCodeOverride: string | null = null): ThemeDTO {
    return {
      title: config.title,
      brandingCode: brandingCodeOverride ?? config.brandingCode,
      qrPath: config.qrPath ?? this.getQrDomain(config.clientDomainKey),
      tileOrder: config.tileOrder ?? [],
      icons: {
        ...this.getDefaultIcons(config.clientDomainKey),
        ...config.icons,
      },
    };
  }
  private getQrDomain(clientDomainKey: string): string {
    if (clientDomainKey === environment.defaultClientDomainKey) {
      return environment.defaultHost;
    }

    return `${clientDomainKey}${environment.defaultDomainSuffix}`;
  }

  private getDefaultIcons(clientDomainKey: string): ThemeIcons {
    const basePath = `/clients/${clientDomainKey}/icons`;

    return {
      favicon: `${basePath}/Icon_48.png`,
      favicon96: `${basePath}/Icon_96.png`,
      faviconSvg: `${basePath}/Icon.svg`,
      faviconIco: `${basePath}/Icon_48.png`,
      appleTouchIcon: `${basePath}/Icon_192.png`,
      manifest192: `${basePath}/Icon_192.png`,
      manifest512: `${basePath}/Icon_512.png`,
    };
  }
}
